/**
 * Stress Testing types — mirror cx-alm DTOs.
 *
 * Source of truth (server-side):
 *   services/cx-alm/.../presentation/dto/StressScenarioDto.java
 *   services/cx-alm/.../presentation/dto/StressRunDto.java
 *
 * NULL shock fields mean "use the live tenant parameter" when the engine runs.
 * BigDecimal values arrive as decimal strings — format only at render time.
 */

/** Scenario classification — matches StressScenario.ScenarioType. */
export type ScenarioType = 'MANUAL' | 'BAM_STANDARD' | 'CRISIS' | 'REGULATORY';

/** Lifecycle state — matches StressScenario.Status. */
export type ScenarioStatus = 'DRAFT' | 'ACTIVE' | 'ARCHIVED';

export interface StressScenario {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  scenarioType: ScenarioType;
  status: ScenarioStatus;

  /** Parallel yield-curve shift in bp. null = use tenant nimDefaultShockBp. */
  rateShockBp: number | null;

  // LCR run-off overrides (null = use tenant parameter)
  lcrRunoffRetailStable:            string | null;
  lcrRunoffRetailLessStable:        string | null;
  lcrRunoffWholesaleOperational:    string | null;
  lcrRunoffWholesaleNonOperational: string | null;

  // HQLA haircut overrides (null = use tenant parameter)
  lcrHqlaLevel2aHaircut: string | null;
  lcrHqlaLevel2bHaircut: string | null;

  /** NIM parallel shock in bp. null = use tenant nimDefaultShockBp. */
  nimShockBp: number | null;

  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

/** Request body for POST /api/v1/alm/stress/scenarios. */
export interface CreateStressScenarioRequest {
  name: string;
  description?: string;
  scenarioType?: ScenarioType;
  rateShockBp?: number | null;
  lcrRunoffRetailStable?:            string | null;
  lcrRunoffRetailLessStable?:        string | null;
  lcrRunoffWholesaleOperational?:    string | null;
  lcrRunoffWholesaleNonOperational?: string | null;
  lcrHqlaLevel2aHaircut?: string | null;
  lcrHqlaLevel2bHaircut?: string | null;
  nimShockBp?: number | null;
}

export interface StressRun {
  id: string;
  tenantId: string;
  scenarioId: string;
  scenarioName: string;
  calculationDate: string;       // ISO yyyy-MM-dd
  engineVersion: string;
  triggeredBy: string;

  // Base metrics
  baseLcrRatio:     string | null;
  baseNsfrRatio:    string | null;
  baseNimBaseline:  string | null;
  baseEveWorstCase: string | null;

  // Stressed metrics
  stressedLcrRatio:     string | null;
  stressedNsfrRatio:    string | null;
  stressedNim:          string | null;
  stressedEveWorstCase: string | null;

  // Deltas (stressed − base)
  lcrDelta:  string | null;
  nsfrDelta: string | null;
  nimDelta:  string | null;
  eveDelta:  string | null;

  // Compliance under stress (null = engine could not determine)
  stressedLcrCompliant:  boolean | null;
  stressedNsfrCompliant: boolean | null;

  // Data availability
  lcrDataAvailable: boolean;
  nimDataAvailable: boolean;
  eveDataAvailable: boolean;

  createdAt: string;
}

/** Human-readable labels for scenario types. */
export const SCENARIO_TYPE_LABELS: Record<ScenarioType, string> = {
  MANUAL:       'Manual',
  BAM_STANDARD: 'BAM Standard',
  CRISIS:       'Crisis',
  REGULATORY:   'Regulatory',
};

/** Human-readable labels for status. */
export const STATUS_LABELS: Record<ScenarioStatus, string> = {
  DRAFT:    'Draft',
  ACTIVE:   'Active',
  ARCHIVED: 'Archived',
};

/** Format a BigDecimal ratio as a percentage string, e.g. "1.2345" → "123.45 %" */
export function fmtRatio(v: string | null | undefined, decimals = 1): string {
  if (v == null) return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : `${(n * 100).toFixed(decimals)} %`;
}

/** Format a BigDecimal as a plain decimal with sign, e.g. "-0.0234" → "−2.3 pp" */
export function fmtDelta(v: string | null | undefined, decimals = 1): string {
  if (v == null) return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${(n * 100).toFixed(decimals)} pp`;
}

/** Format a BigDecimal currency amount with thousands separator. */
export function fmtAmount(v: string | null | undefined, decimals = 0): string {
  if (v == null) return '—';
  const n = parseFloat(v);
  return isNaN(n)
    ? '—'
    : n.toLocaleString('fr-MA', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}
